import type { IntentName, ToolResult } from './types';
import { formatNumber } from '@/lib/number-format';

// ---- Response templates keyed by intent ----

const RESPONSE_TEMPLATES: Record<IntentName, string[]> = {
  greeting: [
    `Hello! I'm your Rugira trading assistant. I can help you with:

- Checking your agent status and performance
- Understanding trading strategies
- Diagnosing issues with your trading agents
- Connecting exchanges and managing positions
- Navigating compliance and risk settings

What would you like help with?`,
  ],
  bot_status: [
    `Let me check the status of your agents.

{{toolResult:mockGetMyBots}}

Would you like to see more details about a specific agent?`,
  ],
  bot_list: [
    `Here are your current trading agents:

{{toolResult:mockGetMyBots}}

You can ask me about any specific agent by name or ID.`,
  ],
  bot_diagnose: [
    `Let me help you diagnose the issue with your agent.

{{toolResult:mockGetBotStatus}}

**Common causes for agent stoppage:**
1. **API Key Expired** -- Check your exchange connection in the Connections page
2. **Insufficient Balance** -- Your trading account may lack funds for the configured position size
3. **Compliance Hold** -- A compliance rule may have triggered a pause
4. **Exchange Rate Limit** -- The agent may have been rate-limited by the exchange

Would you like me to walk you through a diagnostic flow?`,
  ],
  bot_create_help: [
    `Here's how to deploy a trading agent:

1. **Go to Agents** (sidebar > Run > Agents)
2. **Review the mandate and policy boundary**
3. **Select a strategy** from the template library or configure a custom one
4. **Choose your exchange** -- make sure it's connected first (Connections page)
5. **Set your trading pair** (e.g., BTC/USDT)
6. **Configure risk parameters** -- position size, stop-loss, take-profit
7. **Start in Paper Mode** first to validate under the agent mandate

Would you like help with any of these steps?`,
  ],
  agent_workflow: [
    `I can prepare the next safe agent action from the current control context.

{{toolResult:mockGetAgentWorkflowContext}}

**Governed next step:** choose whether you want a mandate draft, a policy-gate brief, or a pause/halt checklist. I will not route a live order from chat; execution remains behind risk, policy, OMS, and audit controls.`,
  ],
  recent_orders: [
    `Here are your recent orders:

{{toolResult:mockGetRecentOrders}}

Need details about a specific order?`,
  ],
  positions: [
    `Here are your current open positions:

{{toolResult:mockGetPositions}}

Would you like to review governed position controls?`,
  ],
  portfolio_overview: [
    `Here's your portfolio overview:

{{toolResult:mockGetPortfolio}}

Want me to explain any of these metrics?`,
  ],
  risk_help: [
    `**Risk control surfaces in Rugira:**

1. **Position sizing limits**: cap notional exposure per agent and per mandate.
2. **Stop-loss controls**: define desk-approved stop behavior before an agent can route proposals.
3. **Portfolio concentration checks**: review exposure across pairs, venues, and strategies.
4. **Max drawdown gates**: set portfolio and agent-level drawdown thresholds for review.
5. **Correlation monitoring**: flag agents with overlapping market exposure before scaling them.

You can configure these in **Settings > Risk Management** or per-agent in the Agent Control Room. I can draft a configuration checklist, but I will not provide investment advice or route a live order from chat.`,
  ],
  strategy_help: [
    `**Available Strategy Types in Rugira:**

- **Grid Trading**: Places buy/sell orders at set intervals. Best for ranging markets.
- **Momentum/Trend Following**: Rides price trends using moving averages/RSI. Best for trending markets.
- **Mean Reversion**: Buys dips and sells rallies. Best for stable-range assets.
- **DCA (Dollar Cost Averaging)**: Regular interval purchases. Lower risk, long-term focus.
- **Arbitrage**: Exploits price differences across venues. Requires multiple exchange connections.

Each strategy has configurable parameters. Visit **Strategies** in the sidebar to explore templates.

Which strategy type interests you?`,
  ],
  strategy_action: [
    `Here are strategy actions I can prepare from the catalog:

{{toolResult:mockGetStrategyCatalog}}

I can turn a selected strategy into an agent draft with mode, venues, limits, and required validation steps before anything reaches execution.`,
  ],
  backtest_help: [
    `**How to Run a Backtest:**

1. Navigate to **Backtesting** (sidebar > Build > Backtesting)
2. Click **"New Backtest"**
3. Select your strategy and parameters
4. Choose the **date range** (minimum 3 months recommended)
5. Set **initial capital** and slippage assumptions
6. Click **"Start Backtest"**

**Interpreting Results:**
- **Total Return**: Overall P&L over the period
- **Sharpe Ratio**: Risk-adjusted return (aim for > 1.0)
- **Max Drawdown**: Worst peak-to-trough decline
- **Win Rate**: Profitable trades / total trades
- **Profit Factor**: Gross profit / gross loss (aim for > 1.5)

Would you like help interpreting specific backtest results?`,
  ],
  backtest_action: [
    `I can use the selected backtest context to prepare a governed next step.

{{toolResult:mockGetBacktestSummary}}

**Safe action:** create a draft agent mandate or a new backtest configuration. Production routing remains blocked until the strategy, mandate, and policy gates pass.`,
  ],
  connect_exchange: [
    `**How to Connect an Exchange:**

1. Go to **Connections** (sidebar > Build > Connections)
2. Click **"Add Connection"**
3. Select your exchange (Binance, Coinbase Pro, Kraken, Bybit, OKX, KuCoin)
4. Enter your **API Key** and **API Secret**
   - Make sure to enable **Read** and **Trade** permissions
   - **Do NOT** enable Withdraw permissions
   - Consider IP whitelisting for security
5. Click **"Test Connection"** to verify
6. Save

**Security tip**: Use a separate API key for Rugira with minimal permissions needed.

Which exchange would you like to connect?`,
  ],
  connection_audit: [
    `I can audit connection readiness for agent use.

{{toolResult:mockGetConnectionPosture}}

**Safe action:** prepare a fix checklist or evidence export. I cannot read secrets or broaden credential scopes from chat.`,
  ],
  explain_error: [
    `Let me help you understand this error.

**Error:** {{errorCode}}
**What happened:** {{event}}
**Impact:** {{impact}}
**Next step:** {{nextStep}}

**When to contact support:** If this error persists after following the steps above, contact support@rugira.ch with the error code and timestamp.`,
  ],
  explain_notification: [
    `I can help explain notifications! You can:
- Click "Ask about this error" on any error toast notification
- Paste an error code (e.g., GW-BOT-001) here
- Describe what happened

What error or notification are you seeing?`,
  ],
  monitoring_triage: [
    `I can triage the current monitoring state and prepare an operator checklist.

{{toolResult:mockGetMonitoringHealth}}

{{toolResult:mockGetAlerts}}

**Safe action:** identify affected agents, prepare a restart checklist, or draft an incident note. Restarts and live controls still require governed service actions.`,
  ],
  compliance_help: [
    `**Compliance Features in Rugira:**

- **Non-Custodial Controls**: Rugira never holds your funds. All connections are read+trade only.
- **KYT (Know Your Transaction)**: Integrated with Chainalysis for transaction monitoring
- **Approval Workflows**: Multi-signature approvals for high-value operations
- **Audit Trails**: Immutable logs of all actions, viewable in Admin > Logs
- **Kill Switch**: Emergency halt of trading agents before new routing

Visit **Compliance** (sidebar > Govern > Compliance) to see your current status.

What specific compliance topic do you need help with?`,
  ],
  compliance_review: [
    `I can prepare a compliance review packet.

{{toolResult:mockGetComplianceQueue}}

**Safe action:** draft acknowledgement rationale, summarize evidence, or prepare an export. I will not mark alerts acknowledged without an explicit governed UI action.`,
  ],
  tax_reconcile: [
    `I can help reconcile the tax pack before export.

{{toolResult:mockGetTaxReconciliation}}

**Safe action:** prepare a warning-by-warning review checklist and export readiness note. Final filing decisions remain with the operator.`,
  ],
  report_analysis: [
    `I can explain the analytics view and prepare a reporting note.

{{toolResult:mockGetReportSnapshot}}

**Safe action:** draft an investment committee summary, identify underperforming agents, or prepare a follow-up analysis task.`,
  ],
  workspace_action: [
    `Here are the actions I can launch from this page through the governed AI box.

{{toolResult:mockGetPageActionCatalog}}

Choose an action chip to create a durable Agent Runtime run. Mutating or sensitive actions stay draft-only unless a future policy-gated execution path explicitly allows them.`,
  ],
  kb_search: [
    `Let me search our knowledge base for that.

{{toolResult:mockSearchKB}}

Would you like more details on any of these topics?`,
  ],
  fallback: [
    `I'm not sure I understood that. I can help with:

- **Trading agent management**: status, diagnostics, mandates
- **Trading**: orders, positions, portfolio overview
- **Strategy**: choosing, configuring, backtesting strategies
- **Technical**: connecting exchanges, error explanations
- **Compliance**: regulations, audit, approvals

Could you rephrase your question, or pick one of these topics?`,
    `I didn't quite catch that. Here are some things you can ask me:

- "Show me my agent status"
- "What are my open positions?"
- "Help me connect Binance"
- "Explain this error"
- "How do I run a backtest?"

Try one of these, or describe your question differently.`,
  ],
};

// ---- Template rendering ----

interface RenderContext {
  toolResults?: ToolResult[];
  entities?: Record<string, string | undefined>;
  attachments?: Array<{ type: string; data: Record<string, unknown> }>;
}

function formatToolData(toolName: string, data: unknown): string {
  if (!data || typeof data !== 'object') return '';

  const d = data as Record<string, unknown>;

  switch (toolName) {
    case 'mockGetMyBots': {
      const bots = d.bots as Array<Record<string, unknown>> | undefined;
      if (!bots?.length) return '_No agents found._';
      const lines = bots.map(bot => {
        const perf = bot.performance as Record<string, unknown> | undefined;
        const pnl = perf?.pnl ?? 0;
        const pnlNum = Number(pnl);
        const pnlStr = pnlNum >= 0 ? `+$${formatNumber(pnlNum, { decimals: 2 })}` : `-$${formatNumber(Math.abs(pnlNum), { decimals: 2 })}`;
        return `| ${bot.name} | ${bot.status} | ${pnlStr} | ${bot.tradingPair} |`;
      });
      return `| Agent | Status | P&L | Pair |\n|-------|--------|-----|------|\n${lines.join('\n')}`;
    }
    case 'mockGetBotStatus': {
      const bot = d as Record<string, unknown>;
      const status = bot.status ?? 'unknown';
      const errorMsg = bot.errorMessage ? `\n**Error:** ${bot.errorMessage}` : '';
      const errorCodeStr = bot.errorCode ? ` (${bot.errorCode})` : '';
      return `**Agent:** ${bot.name ?? 'Unknown'}\n**Status:** ${status}${errorCodeStr}${errorMsg}`;
    }
    case 'mockGetRecentOrders': {
      const orders = d.orders as Array<Record<string, unknown>> | undefined;
      if (!orders?.length) return '_No recent orders._';
      const lines = orders.map(o => {
        const pnl = Number(o.pnl ?? 0);
        const pnlStr = pnl >= 0 ? `+$${formatNumber(pnl, { decimals: 2 })}` : `-$${formatNumber(Math.abs(pnl), { decimals: 2 })}`;
        return `| ${o.symbol} | ${o.side} | ${o.quantity} | $${formatNumber(Number(o.price), { decimals: 2 })} | ${pnlStr} |`;
      });
      return `| Symbol | Side | Qty | Price | P&L |\n|--------|------|-----|-------|-----|\n${lines.join('\n')}`;
    }
    case 'mockGetPositions': {
      const positions = d.positions as Array<Record<string, unknown>> | undefined;
      if (!positions?.length) return '_No open positions._';
      const lines = positions.map(p => {
        const upnl = Number(p.unrealizedPnl ?? 0);
        const upnlStr = upnl >= 0 ? `+$${formatNumber(upnl, { decimals: 2 })}` : `-$${formatNumber(Math.abs(upnl), { decimals: 2 })}`;
        return `| ${p.symbol} | ${p.side} | ${p.quantity} | $${formatNumber(Number(p.avgEntryPrice), { decimals: 2 })} | $${formatNumber(Number(p.markPrice), { decimals: 2 })} | ${upnlStr} |`;
      });
      return `| Symbol | Side | Qty | Entry | Mark | Unrealized P&L |\n|--------|------|-----|-------|------|-----------|\n${lines.join('\n')}`;
    }
    case 'mockGetPortfolio': {
      const pnlDay = Number(d.pnlDay ?? 0);
      const pnlDayStr = pnlDay >= 0 ? `+$${formatNumber(pnlDay, { decimals: 2 })}` : `-$${formatNumber(Math.abs(pnlDay), { decimals: 2 })}`;
      const pnlTotal = Number(d.pnlTotal ?? 0);
      const pnlTotalStr = pnlTotal >= 0 ? `+$${formatNumber(pnlTotal, { decimals: 2 })}` : `-$${formatNumber(Math.abs(pnlTotal), { decimals: 2 })}`;
      return [
        `**Equity:** $${formatNumber(Number(d.equity ?? 0))}`,
        `**Day P&L:** ${pnlDayStr} (${formatNumber(Number(d.pnlDayPct ?? 0), { decimals: 2 })}%)`,
        `**Total P&L:** ${pnlTotalStr}`,
        `**Sharpe Ratio:** ${formatNumber(Number(d.sharpeRatio ?? 0), { decimals: 2 })}`,
        `**Max Drawdown:** ${formatNumber(Number(d.drawdownPct ?? 0), { decimals: 1 })}%`,
        `**VaR (95%):** $${formatNumber(Number(d.var95 ?? 0))}`,
      ].join('\n');
    }
    case 'mockGetAlerts': {
      const alerts = d.alerts as Array<Record<string, unknown>> | undefined;
      if (!alerts?.length) return '_No recent alerts._';
      return alerts.map(a => `- **[${a.severity}]** ${a.title}: ${a.message}`).join('\n');
    }
    case 'mockGetConnections': {
      const connections = d.connections as Array<Record<string, unknown>> | undefined;
      if (!connections?.length) return '_No connections configured._';
      const lines = connections.map(c => `| ${c.name} | ${c.type} | ${c.status} |`);
      return `| Name | Type | Status |\n|------|------|--------|\n${lines.join('\n')}`;
    }
    case 'mockGetAgentWorkflowContext': {
      const attention = d.attention as string[] | undefined;
      const allowedActions = d.allowedActions as string[] | undefined;
      const blockedActions = d.blockedActions as string[] | undefined;
      return [
        `**Attention queue:**\n${attention?.map(item => `- ${item}`).join('\n') ?? '- No active queue items.'}`,
        `**Available assistant actions:** ${(allowedActions ?? []).join(', ')}`,
        `**Blocked from chat:** ${(blockedActions ?? []).join(', ')}`,
      ].join('\n\n');
    }
    case 'mockGetConnectionPosture': {
      const stale = d.stale as string[] | undefined;
      return [
        `**Connections:** ${d.totalConnections} total, ${d.compliant} compliant, ${d.violations} violations`,
        `**Credential posture:** ${d.posture}`,
        `**Needs review:** ${stale?.length ? stale.join(', ') : 'No stale connections detected'}`,
      ].join('\n');
    }
    case 'mockGetStrategyCatalog': {
      const candidates = d.candidates as Array<Record<string, unknown>> | undefined;
      if (!candidates?.length) return '_No strategy candidates available._';
      const lines = candidates.map(item => `| ${item.name} | ${item.risk} | ${item.action} |`);
      return `| Strategy | Risk | Suggested action |\n|---|---|---|\n${lines.join('\n')}`;
    }
    case 'mockGetBacktestSummary': {
      const selectedRuns = d.selectedRuns as Array<Record<string, unknown>> | undefined;
      if (!selectedRuns?.length) return '_No selected backtests available._';
      const lines = selectedRuns.map((run) => {
        const returnPct = formatNumber(Number(run.returnPct ?? 0), { decimals: 2 });
        const sharpe = formatNumber(Number(run.sharpe ?? 0), { decimals: 2 });
        const drawdown = formatNumber(Number(run.maxDrawdownPct ?? 0), { decimals: 2 });
        return `| ${run.strategy} | +${returnPct}% | ${sharpe} | ${drawdown}% |`;
      });
      return `| Strategy | Return | Sharpe | Max drawdown |\n|---|---:|---:|---:|\n${lines.join('\n')}\n\n**Draft candidate:** ${d.recommendedDraft}`;
    }
    case 'mockGetMonitoringHealth': {
      const watchItems = d.watchItems as string[] | undefined;
      const nextActions = d.nextActions as string[] | undefined;
      return [
        `**Status:** ${d.status}`,
        `**Watch items:**\n${watchItems?.map(item => `- ${item}`).join('\n') ?? '- None'}`,
        `**Next actions:** ${(nextActions ?? []).join(', ')}`,
      ].join('\n\n');
    }
    case 'mockGetComplianceQueue': {
      const reviewFocus = d.reviewFocus as string[] | undefined;
      const safeActions = d.safeActions as string[] | undefined;
      return [
        `**Open alerts:** ${d.openAlerts} (${d.critical} critical)`,
        `**Review focus:** ${(reviewFocus ?? []).join(', ')}`,
        `**Safe assistant actions:** ${(safeActions ?? []).join(', ')}`,
      ].join('\n');
    }
    case 'mockGetTaxReconciliation': {
      const warnings = d.warnings as string[] | undefined;
      return [
        `**Warnings:**\n${warnings?.map(item => `- ${item}`).join('\n') ?? '- None'}`,
        `**Export readiness:** ${d.exportReadiness}`,
      ].join('\n\n');
    }
    case 'mockGetReportSnapshot': {
      const drivers = d.drivers as string[] | undefined;
      const nextActions = d.nextActions as string[] | undefined;
      return [
        `**Drivers:**\n${drivers?.map(item => `- ${item}`).join('\n') ?? '- No driver data available.'}`,
        `**Next actions:** ${(nextActions ?? []).join(', ')}`,
      ].join('\n\n');
    }
    case 'mockGetPageActionCatalog': {
      const actions = d.actions as Array<Record<string, unknown>> | undefined;
      const blockedFromChat = d.blockedFromChat as string[] | undefined;
      if (!actions?.length) return '_No page actions available._';
      const lines = actions.map(action => {
        const mode = action.executionMode === 'proposal_draft' ? 'draft run' : 'brief run';
        return `| ${action.label} | ${action.capability} | ${mode} |`;
      });
      return [
        `**Page:** ${d.label}`,
        `| Action | Capability | AI launch mode |\n|---|---|---|\n${lines.join('\n')}`,
        `**Blocked from chat:** ${(blockedFromChat ?? []).join(', ')}`,
      ].join('\n\n');
    }
    case 'mockSearchKB': {
      const articles = d.articles as Array<Record<string, unknown>> | undefined;
      if (!articles?.length) return "_I couldn't find relevant articles. Try rephrasing your question._";
      return articles.map(a => `- **${a.title}**: ${(a.body as string)?.slice(0, 120)}...`).join('\n');
    }
    default:
      return JSON.stringify(data, null, 2);
  }
}

export function renderTemplate(
  intent: IntentName,
  context: RenderContext = {},
): string {
  const templates = RESPONSE_TEMPLATES[intent];
  const template = templates[Math.floor(Math.random() * templates.length)];

  let rendered = template;

  // Replace tool result placeholders
  if (context.toolResults?.length) {
    for (const result of context.toolResults) {
      const placeholder = `{{toolResult:${result.tool}}}`;
      if (rendered.includes(placeholder)) {
        rendered = rendered.replace(placeholder, formatToolData(result.tool, result.data));
      }
    }
  }

  // Replace entity placeholders from attachments (for explain_error)
  if (context.attachments?.length) {
    const errorAttachment = context.attachments.find(a => a.type === 'error_context');
    if (errorAttachment) {
      const d = errorAttachment.data;
      rendered = rendered.replace('{{errorCode}}', String(d.errorCode ?? 'Unknown'));
      rendered = rendered.replace('{{event}}', String(d.event ?? 'An error occurred'));
      rendered = rendered.replace('{{impact}}', String(d.impact ?? 'The operation may not have completed'));
      rendered = rendered.replace('{{nextStep}}', String(d.nextStep ?? 'Check the monitoring page for details'));
    }
  }

  // Replace entity placeholders
  if (context.entities) {
    for (const [key, value] of Object.entries(context.entities)) {
      if (value) {
        rendered = rendered.replaceAll(`{{${key}}}`, value);
      }
    }
  }

  // Clean up any remaining unreplaced placeholders
  rendered = rendered.replace(/\{\{toolResult:\w+\}\}/g, '');
  rendered = rendered.replace(/\{\{\w+\}\}/g, '');

  return rendered.trim();
}

export function getTemplateForIntent(intent: IntentName): string {
  const templates = RESPONSE_TEMPLATES[intent];
  return templates[Math.floor(Math.random() * templates.length)];
}
